import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import moment from 'moment';

import _ from '@lodash';
import { FuseScrollbars } from '@fuse';
import {
	Icon,
	Table,
	TableBody,
	TableCell,
	TablePagination,
	Typography,
	TableRow,
	IconButton,
	Button,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle
} from '@material-ui/core';
import { FuseAnimate } from '@fuse';

import QueueTableHead from './QueueTableHead';
import Error from 'app/fuse-layouts/shared-components/Error';

import * as ActionsDialog from 'app/store/actions';
import * as JobActions from 'app/main/job-manager/store/actions';
import { handleCancelJob } from 'app/main/job-manager/util';

import Popover from '@material-ui/core/Popover';
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state';
import Box from '@material-ui/core/Box';

const QueueTable = props => {
	const dispatch = useDispatch();
	const [data, setData] = useState(null);
	const [page, setPage] = useState(Number(localStorage.getItem('Q-page')) || 0);
	const [rowsPerPage, setRowsPerPage] = useState(Number(localStorage.getItem('Q-rowsPerPage')) || 10);
	const [order, setOrder] = useState({ direction: 'asc', id: null });

	const cluster = useSelector(({ clusters }) => clusters.cluster.cluster);
	const user = useSelector(({ auth }) => auth.user);

	useEffect(() => {
		let filteredData = props.queue.data;
		
		// Apply filter if one is set
		if (props.filter && filteredData) {
			filteredData = filteredData.filter(job => {
				const jobState = job.state.charAt(0);
				switch (props.filter) {
					case 'running':
						return jobState === 'R';
					case 'queued':
						return jobState === 'Q' || jobState === 'P';
					case 'blocked':
						// Blocked includes any state that is not Running, Queued, Pending, or Completed
						return jobState !== 'R' && jobState !== 'Q' && jobState !== 'P' && jobState !== 'C';
					default:
						return true;
				}
			});
		}
		
		setData(filteredData);
	}, [props.queue.data, props.filter]);

	useEffect(() => {
		setPage(0);
	}, [props.filter]);

	const handleRequestSort = (event, property) => {
		if (order.id === property) {
			if (order.direction === 'desc') {
				setOrder({ direction: 'asc', id: property });
			} else {
				setOrder({ direction: 'desc', id: null });
			}
		} else {
			setOrder({ direction: 'desc', id: property });
		}
	};

	const handleChangePage = (event, page) => {
		localStorage.setItem('Q-page', page);
		setPage(page);
	};

	const handleChangeRowsPerPage = event => {
		localStorage.setItem('Q-rowsPerPage', event.target.value);
		setRowsPerPage(event.target.value);
		setPage(page);
	};

	const showConfirmation = selected => {
		dispatch(
			ActionsDialog.openDialog({
				children: (
					<React.Fragment>
						<DialogTitle>WARNING!</DialogTitle>
						<DialogContent>
							<DialogContentText id="queue_table_confirmation-msg">
								This will stop and lose all progress... <br /> Are you sure you want to proceed
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button id="queue_table_confirmation-back-btn" onClick={() => dispatch(ActionsDialog.closeDialog())} color="primary">
								Back
							</Button>
							<Button id="queue_table_confirmation-confirm-btn" onClick={() => cancelSelected(selected)} color="primary" autoFocus>
								Confirm
							</Button>
						</DialogActions>
					</React.Fragment>
				),
				maxWidth: 'sm',
				fullWidth: true,
				onClose: (event, reason) => {
					if (reason !== 'backdropClick') {
						dispatch(ActionsDialog.closeDialog());
					}
				}
			})
		);
	};

	const cancelSelected = selected => {
		handleCancelJob(dispatch, JobActions, user, selected.id, cluster.id).catch(err =>
			dispatch(
				ActionsDialog.openDialog({
					children: <Error message={err.data.message} />,
					maxWidth: 'xs',
					fullWidth: true,
					onClose: (event, reason) => {
						if (reason !== 'backdropClick') {
							dispatch(ActionsDialog.closeDialog());
						}
					}
				})
			)
		);
		dispatch(ActionsDialog.closeDialog());
	};

	const formatNodeCount = nodes => {
		const num = Number(nodes);

		if (isNaN(num)) {
			return (
				<PopupState variant="popover" popupid="queue_table_demo-popup-popover">
					{popupState => (
						<React.Fragment>
							<span className="cursor-pointer text-blue underline font-bold" {...bindTrigger(popupState)}>
								{nodes.length}
							</span>
							<Popover
								{...bindPopover(popupState)}
								anchorOrigin={{
									vertical: 'center',
									horizontal: 'left'
								}}
								transformOrigin={{
									vertical: 'center',
									horizontal: 'right'
								}}
							>
								<Box p={2}>
									{nodes.map((element, i) => {
										return <Typography key={i}>{element}</Typography>;
									})}
								</Box>
							</Popover>
						</React.Fragment>
					)}
				</PopupState>
			);
		} else {
			return num;
		}
	};

	if (!data || (data && data.length === 0)) {
		let display = 'Queued';
		if (props.filter === 'running') display = 'Running';
		else if (props.filter === 'queued') display = 'Queued';
		else if (props.filter === 'blocked') display = 'Blocked';
		else if (props.filter && props.filter === 'R') display = 'Running';
		else if (props.filter && props.filter === 'C') display = 'Completed';
		else if (props.filter && props.filter === 'A') display = 'Queued';
		return (
			<div className="flex flex items-center justify-center h-full">
				<Typography color="textSecondary" variant="h5">
					{`There are no Jobs ${display}...`}
				</Typography>
			</div>
		);
	}

	//convert to int
	data.map(n => {
		n.id = parseInt(n.id);
		return 0;
	});

	return (
		<FuseAnimate animation="transition.slideUpIn" delay={300}>
			<div className="w-full h-full flex flex-col border-1 rounded-8">
				<div className="flex-grow overflow-x-auto overflow-y-auto">
					<Table stickyHeader aria-labelledby="tableTitle">
						<QueueTableHead order={order} onRequestSort={handleRequestSort} />
						<TableBody>
							{(order.id ? _.orderBy(data, [o => o[order.id]], [order.direction]) : data)
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map(n => {
									const state = { icon: '', color: '', state: '' };
									switch (n.state.charAt(0)) {
										case 'R': {
											state.icon = 'directions_run';
											state.color = 'orange';
											state.state = 'Running';
											break;
										}
										case 'Q': {
											state.icon = 'queue';
											state.color = 'black';
											state.state = 'Queued';
											break;
										}
										case 'P': {
											state.icon = 'queue';
											state.color = 'black';
											state.state = 'Pending';
											break;
										}
										case 'C': {
											state.icon = 'check_circle';
											state.color = 'green';
											state.state = 'Completed';
											break;
										}
										default: {
											state.icon = 'indeterminate_check_box';
											state.color = 'red';
											state.state = 'Failed';
										}
									}

									return (
										<TableRow className="h-64 " tabIndex={-1} id={n.name} key={n.id}>
											<TableCell
												id={`queue-id-${n.name}`}
												component="th"
												scope="row"
												align="right"
												className="font-bold"
											>
												{n.id}
											</TableCell>

											<TableCell id={`queue-name-${n.name}`} className="truncate" component="th" scope="row">
												<Typography className="font-bold text-16">{n.name}</Typography>
											</TableCell>

											<TableCell id={`queue-state-${n.name}`} component="th" scope="row" align="left">
												<div className="flex items-center">
													<div className="pr-2">
														<Icon className={`text-${state.color} text-20`}>{state.icon}</Icon>
													</div>
													<div className="pb-1 font-bold">{state.state}</div>
												</div>
											</TableCell>

											<TableCell id={`queue-time-${n.name}`} component="th" scope="row" align="left">
												<Typography className="font-bold">{moment.unix(n.submitted).format('LLL')}</Typography>
											</TableCell>

											<TableCell id={`queue-cores-${n.name}`} component="th" scope="row" align="center">
												<Typography className="font-bold">{n.cores}</Typography>
											</TableCell>

											<TableCell id={`queue-nodes-${n.name}`} component="th" scope="row" align="center">
												<Typography className="font-bold">{formatNodeCount(n.nodes)}</Typography>
											</TableCell>
											<TableCell 
												id={`${n.name}-actions`} 
												align="center"
												style={{
													width: 100,
													minWidth: 100,
													maxWidth: 100,
													position: 'sticky',
													right: 0,
													backgroundColor: '#fff',
													zIndex: 1,
													boxShadow: '-2px 0 5px -2px rgba(0,0,0,0.1)'
												}}
											>
												<IconButton
													disabled={n.state === 'C'}
													className="cursor-pointer"
													onClick={() => showConfirmation(n)}
												>
													<Icon>cancel</Icon>
												</IconButton>
											</TableCell>
										</TableRow>
									);
								})}
						</TableBody>
					</Table>
				</div>
				<TablePagination
					className="mt-auto flex-shrink-0"
					component="div"
					count={data.length}
					rowsPerPage={rowsPerPage}
					page={page}
					backIconButtonProps={{ 'aria-label': 'Previous Page' }}
					nextIconButtonProps={{ 'aria-label': 'Next Page' }}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</div>
		</FuseAnimate>
	);
};

export default withRouter(QueueTable);
